import { create } from 'zustand';
import { TSearchAvailabilityStore } from './domain';
import { DEFAULT_SEARCH_AVAILABILITY_STATE } from './state';
import { format, setDate, startOfDay } from 'date-fns';
import { MONTH_FORMAT_DATE, SERVER_FORMAT_DATE } from '@/service/constants/dates';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { getAvailabilityByMonth } from '@/service/api/Availability/getAvailabilityByMonth';
import { isExpired10mins } from '@/service/utils/isExpired10mins';
import { getAvailabilityByDay } from '@/service/api/Availability/getAvailabilityByDay';
import { getPricesForSchedules } from '@/service/api/Prices/getPricesForSchedules';
import { mapSchedulesWithPrices } from '@/service/utils/mapSchedulesWithPrices';
import { TAvailByDayResp } from '@/domain/Availability.domain';

export const useSearchAvailabilityStore = create<TSearchAvailabilityStore>((set, get) => ({
    ...DEFAULT_SEARCH_AVAILABILITY_STATE,
    initDate: async (partnerSlug, activities, date) => {
        set({
            activities,
            partnerSlug,
            isMonthLoading: true,
        });

        const newDate = date || get().selectedDate || new Date();

        if (!get().selectedDate && !!date) {
            set({
                selectedDate: newDateFromString(date),
            });
        }

        const currentDate = newDate ? newDateFromString(newDate) : startOfDay(new Date());
        const month = format(currentDate, MONTH_FORMAT_DATE);
        const activitiesList = activities.split(',');

        activitiesList.forEach((id) =>
            get()._setMonth(partnerSlug, id, month, currentDate.getMonth())
        );
    },
    _setMonth: async (partnerSlug, tripId, month, monthNumber) => {
        const monthRes = await getAvailabilityByMonth(partnerSlug, tripId, month);

        const selectedMonth = get().months[monthNumber];
        const selectedMonthData = selectedMonth?.data || [];
        const monthData = new Set([...selectedMonthData, ...monthRes]);

        set((state) => ({
            months: {
                ...state.months,
                [monthNumber]: {
                    timestamp: selectedMonth?.timestamp || new Date().getTime(),
                    data: [...monthData],
                },
            },
            isMonthLoading: false,
        }));
    },
    selectDate: (newDate, isInit) => {
        const currentDate = newDate ? newDateFromString(newDate) : startOfDay(new Date());

        set({
            selectedDate: currentDate,
        });

        get().addSchedules(currentDate, isInit);
    },
    addMonth: (date) => {
        const { partnerSlug, activities, initDate, months } = get();

        const newDate = date ? newDateFromString(date) : new Date();
        const loadedMonth = months[newDate.getMonth()];

        if (!loadedMonth || isExpired10mins(loadedMonth.timestamp)) {
            set((state) => ({
                months: { ...state.months, [newDate.getMonth()]: undefined },
            }));
            initDate(partnerSlug, activities, newDate);
        }
    },
    addSchedules: async (date, isInit) => {
        const { partnerSlug, activities, schedules } = get();

        const newDate = date ? newDateFromString(date) : new Date();
        const dayKey = format(newDate, SERVER_FORMAT_DATE);
        const loadedDay = schedules[dayKey];

        if ((!loadedDay || isExpired10mins(loadedDay.timestamp)) && activities) {
            set((state) => ({
                schedules: { ...state.schedules, [dayKey]: undefined },
                isSchedulesLoading: true,
            }));

            const ids = activities.split(',');

            Promise.all(
                ids.map((activityId) => get()._setSchedule(partnerSlug, activityId, dayKey))
            ).then((data) => {
                if (isInit) {
                    const fullData = (data as unknown as TAvailByDayResp[]).flat();

                    const isUnavailDay =
                        !fullData.length ||
                        fullData.every(
                            (item) =>
                                !item?.schedules?.some(
                                    (schedule) => schedule.is_avail || schedule.call_to_booking
                                )
                        );

                    if (isUnavailDay) {
                        const nextAvailDay =
                            get().months[newDate.getMonth()]?.data?.find(
                                (day) => +day > newDate.getDate()
                            ) || new Date().getDate();

                        if (nextAvailDay) {
                            get().selectDate(setDate(newDate, +nextAvailDay));
                        }
                    }
                }

                set({
                    isSchedulesLoading: false,
                });
            });
        }
    },
    _setSchedule: async (partnerSlug, activityId, dayKey) => {
        try {
            const dayRes = await getAvailabilityByDay(partnerSlug, activityId, dayKey);

            const newDayData = dayRes || [];

            const currentData = get().schedules[dayKey];

            set((state) => ({
                schedules: {
                    ...state.schedules,
                    [dayKey]: {
                        timestamp: currentData?.timestamp || new Date().getTime(),
                        data: {
                            ...(currentData?.data && currentData.data),
                            [activityId]: currentData?.data?.[0]
                                ? [
                                      {
                                          ...currentData.data[activityId][0],
                                          ...newDayData[0],
                                      },
                                  ]
                                : newDayData,
                        },
                    },
                },
            }));

            if (dayRes.length) {
                get()._setPrice(partnerSlug, activityId, dayKey);
            }

            return dayRes;
        } catch (err) {
            console.error('[WARN] Error on fetching tripSchedules', activityId, dayKey, err);
            return undefined;
        }
    },
    _setPrice: async (partnerSlug, activityId, dayKey) => {
        const prices = await getPricesForSchedules(partnerSlug, activityId, dayKey, false, false);

        const currentData = get().schedules[dayKey];

        if (prices) {
            set((state) => ({
                schedules: {
                    ...state.schedules,
                    [dayKey]: {
                        timestamp: currentData?.timestamp || new Date().getTime(),
                        data: {
                            ...(!!currentData && currentData.data),
                            [activityId]: currentData?.data?.[activityId]
                                ? mapSchedulesWithPrices(currentData.data[activityId], prices)
                                : mapSchedulesWithPrices([{}] as TAvailByDayResp, prices),
                        },
                    },
                },
            }));
        }
    },
    getSchedules: () => {
        const { selectedDate, schedules } = get();
        const newDate = selectedDate ? newDateFromString(selectedDate) : new Date();

        const dayKey = format(newDate, SERVER_FORMAT_DATE);
        const loadedDay = schedules[dayKey];

        return loadedDay?.data || undefined;
    },
}));
